import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Search, Star, MapPin, CheckCircle2, MessageCircle, Calendar,
  Filter, ChevronDown, SlidersHorizontal,
  Heart, Clock, Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import RequestCallbackButton from "../components/RequestCallbackButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_emergent-website-3/artifacts/6i1oj5oc_BTR_Health_favicon__2_-removebg-preview.png";

// SEO helper
const updatePageMeta = (title, description) => {
  document.title = title;
  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) metaDesc.setAttribute('content', description);
};

// (Filters: gender + language only — categories removed per product decision)

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [sortBy, setSortBy] = useState("default");
  const [genderPref, setGenderPref] = useState(searchParams.get("gender") || "any");
  const [languageFilter, setLanguageFilter] = useState(searchParams.get("language") || "any");
  const [ageGroup, setAgeGroup] = useState(searchParams.get("age") || "any");
  const [pricingCategories, setPricingCategories] = useState([]);

  // Pricing reference (loaded once for cap-badge rendering on each card)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/pricing/categories`);
        if (r.ok) {
          const d = await r.json();
          setPricingCategories(d.registration_groups || []);
        }
      } catch (e) { /* non-blocking */ }
    })();
  }, []);

  const capForWorker = (worker) => {
    const cat = worker.primary_service_category || "daily_personal_activities";
    const g = pricingCategories.find(c => c.key === cat);
    if (!g) return null;
    const standard = g.line_items.find(li => (li.name || "").includes("Standard") && (li.name || "").includes("Daytime"));
    return standard?.weekday ?? g.line_items[0]?.weekday ?? null;
  };

  // SEO
  useEffect(() => {
    updatePageMeta(
      'Find NDIS Support Workers Near You | BTR Health Australia',
      'Browse verified NDIS support workers in your area. Filter by service type, availability, and price. Book instantly with zero fees.'
    );
  }, []);

  useEffect(() => {
    // Seed workers on first visit
    const seed = async () => {
      try {
        // Seed call removed — launch dataset is now managed via admin/seed only
      } catch (e) {}
    };
    seed();
  }, []);

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      let url = `${API}/workers?`;
      if (searchQuery) {
        url += `search=${encodeURIComponent(searchQuery)}&`;
      }
      if (genderPref && genderPref !== "any") {
        url += `gender=${encodeURIComponent(genderPref)}&`;
      }
      if (languageFilter && languageFilter !== "any") {
        url += `language=${encodeURIComponent(languageFilter)}&`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        let data = await response.json();
        
        // Sort workers
        if (sortBy === "rating") {
          data.sort((a, b) => b.rating - a.rating);
        } else if (sortBy === "reviews") {
          data.sort((a, b) => b.review_count - a.review_count);
        }
        
        setWorkers(data);
      }
    } catch (error) {
      console.error("Failed to fetch workers:", error);
      toast.error("Failed to load workers");
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when filters change
  useEffect(() => {
    fetchWorkers();
  }, [searchQuery, sortBy, genderPref, languageFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchWorkers();
  };


  return (
    <div className="min-h-screen bg-slate-50 pb-20 md:pb-8">
      {/* Header */}
      <header className="bg-[#87CEEB] sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_ICON} alt="BTR Health" className="h-7" />
              <span className="text-white font-bold text-lg hidden sm:inline">BTR Health</span>
            </Link>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-white">Find Support</h1>
              <p className="text-sm text-white/80">{workers.length} workers available</p>
            </div>
            <div className="hidden md:flex items-center gap-2">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/20"
                onClick={() => navigate("/worker-signup")}
                data-testid="join-as-worker-btn"
              >
                Join as Worker
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-4 max-w-4xl">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name, skill, or location..."
              className="pl-12 h-12 bg-white border-slate-200 rounded-xl"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
            />
          </div>
        </form>

        {/* Filters: gender / language */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <select
            value={genderPref}
            onChange={(e) => setGenderPref(e.target.value)}
            className="h-9 rounded-full border border-slate-200 bg-white px-3 text-sm text-slate-600"
            data-testid="filter-gender"
          >
            <option value="any">Any gender</option>
            <option value="female">Female worker</option>
            <option value="male">Male worker</option>
            <option value="non_binary">Non-binary</option>
            <option value="no_preference">No preference</option>
          </select>
          <select
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            className="h-9 rounded-full border border-slate-200 bg-white px-3 text-sm text-slate-600"
            data-testid="filter-language"
          >
            <option value="any">Any language</option>
            <option value="English">English</option>
            <option value="Mandarin">Mandarin</option>
            <option value="Cantonese">Cantonese</option>
            <option value="Arabic">Arabic</option>
            <option value="Vietnamese">Vietnamese</option>
            <option value="Spanish">Spanish</option>
            <option value="Hindi">Hindi</option>
            <option value="Tagalog">Tagalog</option>
            <option value="Auslan">Auslan</option>
          </select>
          <select
            value={ageGroup}
            onChange={(e) => setAgeGroup(e.target.value)}
            className="h-9 rounded-full border border-slate-200 bg-white px-3 text-sm text-slate-600"
            data-testid="filter-age"
            aria-label="Participant age range"
          >
            <option value="any">Any age</option>
            <option value="18-24">18–24</option>
            <option value="25-34">25–34</option>
            <option value="35-49">35–49</option>
            <option value="50-64">50–64</option>
            <option value="65+">65+</option>
          </select>
        </div>

        {/* Sort */}
        <div className="flex items-center justify-between mb-4 gap-3">
          <p className="text-slate-600 text-sm">{workers.length} workers found</p>
          <div className="flex items-center gap-2">
            <RequestCallbackButton variant="banner" context="search_results" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-lg">
                  <Filter className="w-4 h-4 mr-2" />
                  Sort
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSortBy("default")}>
                  Featured
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy("rating")}>
                  Highest Rated
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy("reviews")}>
                  Most Reviews
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Sign Up Banner removed — participants book without accounts */}

        {/* Workers List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-4 animate-pulse">
                <div className="flex gap-4">
                  <div className="w-24 h-24 bg-slate-200 rounded-xl" />
                  <div className="flex-1">
                    <div className="h-5 bg-slate-200 rounded w-1/2 mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-1/3 mb-2" />
                    <div className="h-3 bg-slate-200 rounded w-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {workers.map((worker, index) => (
              <motion.div
                key={worker.worker_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(`/worker/${worker.worker_id}`)}
                data-testid={`worker-${worker.worker_id}`}
              >
                <div className="flex gap-4">
                  <div className="relative flex-shrink-0">
                    <img
                      src={worker.picture}
                      alt={worker.name}
                      className="w-24 h-24 object-cover rounded-xl"
                      style={{ objectPosition: worker.picture.includes('ora_bun') ? 'center 30%' : 'center center' }}
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400";
                      }}
                    />
                    {worker.verified && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shadow-sm">
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{worker.name}</h3>
                        {worker.review_count > 50 && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                            Popular
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-slate-400 text-xs italic">Price on request</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 text-sm text-slate-500 mb-2">
                      <div className="flex items-center gap-1">
                        {worker.review_count > 0 ? (
                          <>
                            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                            <span className="font-medium text-slate-700">{Number(worker.rating).toFixed(1)}</span>
                            <span className="text-slate-400">({worker.review_count})</span>
                          </>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
                            <Sparkles className="w-3 h-3" /> New
                          </span>
                        )}
                      </div>
                      <span className="text-slate-300">•</span>
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {worker.location}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {worker.skills.slice(0, 3).map((skill) => (
                        <span key={skill} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-xs">
                          {skill}
                        </span>
                      ))}
                      {worker.skills.length > 3 && (
                        <span className="px-2 py-0.5 text-slate-400 text-xs">
                          +{worker.skills.length - 3} more
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-slate-500 line-clamp-1 mb-3">{worker.bio}</p>
                    
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        className="rounded-lg bg-[#87CEEB] hover:bg-[#6BB8DB] flex-1"
                        onClick={() => navigate(`/worker/${worker.worker_id}`)}
                        data-testid={`book-${worker.worker_id}`}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        View Profile
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {!loading && workers.length === 0 && (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No workers found</h3>
            <p className="text-slate-500">Try adjusting your search or filters</p>
          </div>
        )}
      </main>
    </div>
  );
}
